import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { BaseApiService, ApiResponse } from './base-api.service';
import { Wallet, Facturation, Invoice, Receipt } from '@core/models/wallet.model';
import { environment } from '@environments/environment';
import { NotificationService } from '../notification.service';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { CurrencyIso } from '@core/utils/currency.utils';

interface WalletBalanceResponse extends ApiResponse<number> {
    converted?: { currency: string; balance: number; rate: number; rate_date: string } | null;
}

@Injectable({
    providedIn: 'root',
})
export class WalletApiService extends BaseApiService {
    private notificationService = inject(NotificationService);

    /**
     * The API converts the balances into `currency` (ISO) at the rate of the day and returns the result next to the EUR
     * `balance` (`balance_converted`): the front never converts, it only shows the converted amount in `balance`.
     */
    getWallets(clientIds: string[], currency: CurrencyIso): Observable<Wallet[]> {
        return this.post<ApiResponse<Wallet[]>>(`/wallets`, { clientIds, currency }, { baseUrl: environment.ip_extra_services }).pipe(
            map((response) => {
                if (response.code === 200 && response.data) {
                    return response.data.map((wallet) => {
                        if (!wallet) return wallet;
                        wallet.balance = Math.round((wallet.balance_converted ?? wallet.balance) * 100) / 100;
                        return wallet;
                    });
                }
                return [];
            }),
        );
    }

    /**
     * `currency` is an ISO code. `data` stays the EUR balance, the converted one comes in `converted` next to it:
     * null when the API has no rate for that currency (or an old API that ignores the parameter). For EUR the API may
     * omit `converted` (no conversion to do), so `data` is already the answer.
     */
    getWalletBalance(clientId: string, walletId: string, currency: CurrencyIso): Observable<number | null> {
        const params = new HttpParams().set('currency', currency);
        return this.get<WalletBalanceResponse>(`/wallet/balance/${clientId}/${walletId}`, params, {
            baseUrl: environment.ip_extra_services,
        }).pipe(
            map((response) => {
                if (response.converted) return response.converted.balance;
                return currency === 'EUR' && typeof response.data === 'number' ? response.data : null;
            }),
        );
    }

    getFacturations(clientIds: string[]): Observable<Facturation[]> {
        return this.post<ApiResponse<Facturation[]>>(
            `/billing`,
            { clientIds },
            {
                baseUrl: environment.ip_extra_services,
            },
        ).pipe(
            map((response) => {
                if (response.code === 200 && response.data) {
                    return response.data;
                }
                return [];
            }),
        );
    }

    updateFacturation(clientId: string, facturation: Facturation): Observable<boolean> {
        return this.post<ApiResponse<boolean>>(`/billing/${clientId}`, facturation, { baseUrl: environment.ip_extra_services }).pipe(
            map((response) => {
                if (response.code === 200) {
                    return true;
                }
                throw new Error('Failed to update facturation');
            }),
        );
    }

    downloadInvoicePdf(
        source: Invoice['source'] | Receipt['source'],
        clientId: string,
        factureName: string,
        factureNumber: string,
    ): Observable<Blob> {
        // Silent: the caller shows its own download error toast, the global handler would add a second one.
        return this.get<Blob>(`/download/${source}/${clientId}/${factureName}/${factureNumber}`, undefined, {
            baseUrl: environment.ip_extra_services,
            responseType: 'blob',
            headers: new HttpHeaders({ 'X-Silent-Error': 'true' }),
        });
    }

    getInvoices(clientId: string): Observable<{ invoices: Invoice[]; receipts: Receipt[] }> {
        const headers = new HttpHeaders({
            'X-Silent-Error': 'true',
        });
        return this.get<ApiResponse<{ invoices: Invoice[]; receipts: Receipt[] }>>(`/invoices/${clientId}`, undefined, {
            baseUrl: environment.ip_extra_services,
            headers: headers,
        }).pipe(
            map((response) => {
                if (response.code === 200 && response.data) {
                    return response.data;
                }
                return { invoices: [], receipts: [] };
            }),
            catchError((error) => {
                console.error('Error fetching invoices, returning empty data:', error);
                return of({ invoices: [], receipts: [] });
            }),
        );
    }
}
